import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Icon, Input, Spin, Select, InputNumber, Button, message, Modal, Tag } from "antd"
import { getInformationBatchByName, listPartner, saveDelivery, getSewingThreadWarning } from "../../../services/api/3dApi"
import { v4 as uuidV4 } from 'uuid'
import queryString from 'query-string'
import SewingThreadWarning from '../../update-batches/components/SewingThreadWarning'

const SewingGroupPage = () => {
    const [loading, setLoading] = useState(false)
    const [valueInput, setValueInput] = useState('')
    const [errorInput, setErrorInput] = useState('')
    const [listGroups, setListGroups] = useState([])
    const [sewingGroups, setSewingGroups] = useState([
        {
            id: null,
            quantity: null,
            _idx: uuidV4()
        },
    ])
    const [batchs, setBatchs] = useState([])
    const [detailBatch, setDetailBatch] = useState({})
    const [sewingThreadWarning, setSewingThreadWarning] = useState(null)
    const [openSelect, setOpenSelect] = useState(null)
    const [activeOption, setActiveOption] = useState(null)
    const [autoOpenRow, setAutoOpenRow] = useState(null)
    const { sewing_partner = {}, brand, product_types = [], count_assignable_sewing_items = 0, tags = [] } = { ...detailBatch }
    const total = useMemo(() => batchs.reduce((acc, item) => {
        const { count_assignable_sewing_items = 0 } = Object.assign({}, item)
        return acc + count_assignable_sewing_items
    }, 0), [batchs])

    const groupColumns = useMemo(() => {
        const columns = new Map()

        listGroups.forEach(g => {
            const partnerName = g.partner_name || sewing_partner.name || ''
            if (!columns.has(partnerName)) columns.set(partnerName, [])
            columns.get(partnerName).push(g)
        })

        return [...columns.entries()].map(([partnerName, groups]) => ({
            partnerName,
            groups: [...groups].sort((a, b) => `${a.name}`.localeCompare(`${b.name}`, undefined, { numeric: true }))
        }))
    }, [listGroups, sewing_partner.name])

    const _input = useRef(null)
    const _optionRefs = useRef({})
    const _quantityRefs = useRef({})
    const _scrollToActive = useRef(false)

    useEffect(() => {
        if (!_scrollToActive.current) return
        _scrollToActive.current = false

        const el = _optionRefs.current[`${openSelect}_${activeOption}`]
        if (!!el) el.scrollIntoView({ block: 'nearest' })
    }, [activeOption, openSelect])

    useEffect(() => {
        if (!!_input.current) _input.current.focus()
    }, [])

    // Quét lô thành công / thêm nhóm may thì mở sẵn dropdown chọn nhóm may của dòng tương ứng (không chuyển focus,
    // con trỏ vẫn ở ô nhập tên lô để quét tiếp được)
    useEffect(() => {
        if (!autoOpenRow) return
        setAutoOpenRow(null)

        // Chỉ mở cho dòng còn tồn tại và chưa chọn nhóm (tránh bật lại khi fetch đối tác về chậm)
        if (!groupColumns.length || !sewingGroups.some(s => s._idx === autoOpenRow && !s.id)) return

        _handleDropdownVisibleChange(true, { _idx: autoOpenRow, id: null })
    }, [autoOpenRow]) // eslint-disable-line react-hooks/exhaustive-deps

    const _fetchPartners = async () => {
        try {
            const payload = { hidden: false, type: 'sewing_partner', limit: 1000 }
            const query = queryString.stringify(payload)

            const { data: response } = await listPartner(query)
            const { data, success, error } = response

            if (!success) throw new Error(error)

            const { partners } = data
            const listGroups = partners.map(item => {
                if (!!item.has_groups) {
                    const { groups = [] } = Object.assign({}, item)
                    const newGroups = groups.map(g => ({ ...g, partner_name: item.name })) || []
                    return newGroups
                }
            }).filter(Boolean).flat(Infinity)

            setListGroups(listGroups)
        } catch (e) {
            message.error(e.message)
        }
    }

    const _handleChangeScan = (e) => {
        const { value } = e.target

        setErrorInput('')
        setValueInput(value)
    }

    const _resetScanInput = () => {
        setErrorInput('')
        setValueInput('')
        setLoading(false)
        setSewingThreadWarning(null)

        if (!!_input.current) _input.current.focus()
    }

    const _getSewingThreadWarning = async (batchNames) => {
        if (!Array.isArray(batchNames) || batchNames.length === 0) {
            setSewingThreadWarning(null)
            return
        }
        try {
            const { data: response } = await getSewingThreadWarning({ batchNames })
            const { success, data } = response || {}
            if (!success) {
                setSewingThreadWarning(null)
                return
            }
            setSewingThreadWarning(data)
        } catch (e) {
            setSewingThreadWarning(null)
        }
    }

    const convertTag = (tag) => {
        switch (tag) {
            case 'cutoff':
                return "Cutoff"
            case 'fastproduction':
                return "Sản xuất nhanh"
            case 'tiktok':
                return "TIKTOK"
            case '24h':
                return "24h"
            default:
                return tag
        }
    }

    const _getBatchDetailByName = async (value) => {
        const firstRowIdx = uuidV4()
        let openRowIdx = null

        setLoading(true)
        setSewingGroups([
            {
                id: null,
                quantity: null,
                _idx: firstRowIdx
            },
        ])
        try {
            const { data: response } = await getInformationBatchByName({ name: value })
            const { success, data, error } = response
            if (!success) {
                Modal.error({
                    title: 'Lỗi',
                    content: error,
                    okText: 'Đóng'
                })
                _resetScanInput()
                return
            }

            const { disable, message, batch = {} } = data

            if (message !== '' && disable) {
                Modal.error({
                    title: 'Lỗi',
                    content: data.message,
                    okText: 'Đóng'
                })
                _resetScanInput()
                return
            }

            const { sewing_partner = {}, production_meta = {}, status = '' } = { ...batch }

            if (!['wait_to_sew', 'sewing'].includes(status)) {
                Modal.error({
                    title: 'Lỗi',
                    content: 'Giao nhóm may với các lô ở trạng thái: Chờ may, Đang may.',
                    okText: 'Đóng'
                })
                _resetScanInput()
                if (!batchs.length) setDetailBatch({})
                return
            }
            const { sewing_groups = [] } = { ...production_meta }
            if (!!sewing_groups.length) {
                Modal.error({
                    title: 'Lỗi',
                    content: 'Lô đã được giao cho nhóm may.',
                    okText: 'Đóng'
                })
                _resetScanInput()
                if (!batchs.length) setDetailBatch({})
                return
            }

            const newBatchs = [...new Map([...batchs, batch].map(batch => [batch._id, batch])).values()]
            const sewingPartners = newBatchs.map(item => {
                const { sewing_partner = {} } = item
                return !!sewing_partner.name && sewing_partner.name
            }).filter(Boolean)

            const isValid = new Set(sewingPartners).size <= 1
            if(!isValid) {
                Modal.error({
                    title: 'Lỗi',
                    content: 'Các lô được giao nhóm may phải cùng 1 partner may hoặc chưa có partner may.',
                    okText: 'Đóng'
                })
                _resetScanInput()
                return
            }
            
            if (!!Object.keys(batch).length) setDetailBatch(batch)
            setBatchs(newBatchs)
            _getSewingThreadWarning(newBatchs.map(b => b.name))
            openRowIdx = firstRowIdx

            if (!!Object.keys(sewing_partner).length) {
                const { groups = [], has_groups = false } = { ...sewing_partner }
                setListGroups(has_groups ? groups : [])
            } else {
                if (batchs.some(b => !!b.sewing_partner)) return

                // Không chờ fetch đối tác trong luồng quét để ô quét được giải phóng ngay, fetch xong mới mở dropdown
                openRowIdx = null
                _fetchPartners().then(() => setAutoOpenRow(firstRowIdx))
            }

        } catch (e) {
            Modal.error({
                title: 'Lỗi',
                content: e.message,
                okText: 'Đóng'
            })
        } finally {
            setLoading(false)
            setValueInput('')
            _input.current.focus()
            if (!!openRowIdx) setAutoOpenRow(openRowIdx)
        }
    }

    const _onSubmit = async (e) => {
        e.preventDefault()
        const check = /^\w+-\d/.test(valueInput)

        if (!check) return setErrorInput('Mã lô sai định dạng.')

        const valueFormatted = valueInput.trim()

        if (!valueFormatted) return setErrorInput('Mã lô sai định dạng.')

        check && _getBatchDetailByName(valueFormatted.toUpperCase())
    }

    const _handleChangeSewingGroup = (field, value, idx) => {
        setSewingGroups(preState => {
            const isFirstSelection = !preState.some(item => item.id)
            return preState.map(item => {
                if (idx === item._idx) {
                    if (field === 'id') {
                        const sewingPartner = listGroups.find(g => g._id === value) || {}
                        const { partner_name = '' } = { ...sewingPartner }
                        return {
                            ...item,
                            [field]: value,
                            partner_name,
                            quantity: isFirstSelection ? count_assignable_sewing_items : null
                        }
                    }

                    return { ...item, [field]: value }
                }

                return item
            })
        })
    }

    const _isGroupDisabled = (groupId, rowIdx) => sewingGroups.some(s => s.id === groupId && s._idx !== rowIdx)

    const _getOptionPositions = (rowIdx) => groupColumns.map((column, col) => column.groups.map((g, row) => ({
        id: g._id,
        col,
        row,
        disabled: _isGroupDisabled(g._id, rowIdx)
    }))).flat()

    const _handleDropdownVisibleChange = (open, item) => {
        setOpenSelect(open ? item._idx : null)
        if (!open) return

        const firstEnabled = _getOptionPositions(item._idx).find(o => !o.disabled)
        setActiveOption(item.id || (firstEnabled ? firstEnabled.id : null))
    }

    const _handleSelectGroup = (groupId, item) => {
        if (_isGroupDisabled(groupId, item._idx)) return
        if (item.id !== groupId) _handleChangeSewingGroup('id', groupId, item._idx)
        setOpenSelect(null)

        // Chọn xong nhóm may thì focus sang ô số lượng của dòng đó (bôi đen sẵn để gõ đè)
        if (batchs.length > 1) return
        setTimeout(() => {
            const quantityInput = _quantityRefs.current[item._idx]
            if (!quantityInput) return

            quantityInput.focus()
            if (!!document.activeElement && typeof document.activeElement.select === 'function') document.activeElement.select()
        })
    }

    // Điều hướng bàn phím trong lưới cột: ↑↓ qua lại các option, ←→ sang cột đối tác bên cạnh, Enter để chọn
    const _handleKeyDownSelect = (e, item) => {
        // Gõ/quét ký tự khi đang focus ô chọn nhóm may thì chuyển về ô nhập tên lô để quét tiếp
        if ((e.key || '').length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) {
            setOpenSelect(null)
            if (!!_input.current) _input.current.focus()
            return
        }

        if (openSelect !== item._idx || !groupColumns.length) return

        const positions = _getOptionPositions(item._idx)
        const enabled = positions.filter(o => !o.disabled)
        if (!enabled.length) return

        const current = positions.find(o => o.id === activeOption)
        let next = null

        switch (e.key) {
            case 'ArrowDown':
            case 'ArrowUp': {
                const step = e.key === 'ArrowDown' ? 1 : -1
                const idx = enabled.findIndex(o => o.id === activeOption)
                next = idx === -1 ? enabled[step > 0 ? 0 : enabled.length - 1] : enabled[(idx + step + enabled.length) % enabled.length]
                break
            }
            case 'ArrowLeft':
            case 'ArrowRight': {
                if (!current) {
                    next = enabled[0]
                    break
                }
                const step = e.key === 'ArrowRight' ? 1 : -1
                const totalColumns = groupColumns.length
                for (let i = 1; i < totalColumns && !next; i++) {
                    const col = (current.col + step * i + totalColumns * i) % totalColumns
                    const candidates = enabled.filter(o => o.col === col)
                    if (!candidates.length) continue
                    next = candidates.reduce((nearest, o) => Math.abs(o.row - current.row) < Math.abs(nearest.row - current.row) ? o : nearest)
                }
                break
            }
            case 'Enter':
                e.preventDefault()
                e.stopPropagation()
                if (!!current && !current.disabled) _handleSelectGroup(current.id, item)
                return
            default:
                return
        }

        e.preventDefault()
        e.stopPropagation()
        if (!next) return

        _scrollToActive.current = true
        setActiveOption(next.id)
    }

    // Đang ở ô số lượng mà quét/gõ ký tự không phải số (mã lô) thì chuyển về ô nhập tên lô để quét tiếp
    const _handleKeyDownQuantity = (e) => {
        if ((e.key || '').length !== 1 || /[0-9]/.test(e.key) || e.ctrlKey || e.metaKey || e.altKey) return
        if (!!_input.current) _input.current.focus()
    }

    const _handleAddGroupSewing = () => {
        const newRowIdx = uuidV4()
        setSewingGroups(preState => [...preState, { id: null, quantity: null, _idx: newRowIdx }])
        setAutoOpenRow(newRowIdx)
    }

    const _handleDeleteGroupSewing = (idx) => {
        setSewingGroups(preState => preState.filter(item => item._idx !== idx))
    }

    const checkDuplicatePartner = (items = []) => {
        if (items.length === 1) return true

        const partnerCount = new Map()

        for (const item of items) {
            const name = !!item.partner_name ? item.partner_name : ''
            partnerCount.set(name, (partnerCount.get(name) || 0) + 1)
        }

        const duplicates = [...partnerCount.entries()].filter(([_, count]) => count > 1).map(([name]) => name)

        return duplicates.length > 0
    }

    const _handleDelivery = async () => {
        try {
            const payload = {}
            if (sewingGroups.some(item => !item.id || !item.quantity)) throw new Error("Cần nhập đủ nhóm may và số lượng item.")

            if (!checkDuplicatePartner(sewingGroups) && !sewing_partner.name) throw new Error("Các nhóm sản xuất được chọn phải cùng 1 đối tác.")
            const checkQuantity = sewingGroups.some(group => !!group.quantity && group.quantity < 1)
            if (checkQuantity) throw new Error("Số lượng item giao cho nhóm sản xuất phải ≥ 1.")

            const totalQuantity = sewingGroups.reduce((acc, item) => acc + (+item.quantity || 0), 0) || 0
            if (+totalQuantity !== +count_assignable_sewing_items) throw new Error("Tổng số item giao may phải bằng số item trong lô.")

            const newSewingGroups = sewingGroups.map(group => ({ id: group.id, quantity: +group.quantity })) || []

            if (!!newSewingGroups.length) payload.groups = newSewingGroups

            const { data = {} } = await saveDelivery(detailBatch._id, payload)

            const { success, error } = { ...data }

            if (!success) throw new Error(error)

            message.success('Giao may thành công.')
            _resetScanInput()
            setDetailBatch({})
            setBatchs([])
        } catch (error) {
            message.error(error.message)
        }
    }

    const _handleMultipleDelivery = async () => {
        try {
            const promises = batchs.map(batch => {
                const payload = {
                    groups: [{
                        id: sewingGroups[0].id,
                        quantity: batch.count_assignable_sewing_items
                    }]
                }

                return saveDelivery(batch._id, payload)
                    .then(response => {
                        const { data, success, error } = response.data
                        if (!success) {
                            return { success: false, batchId: batch._id, error: error, batchName: batch.name }
                        }
                        return { success: true, batchId: batch._id, name: data.name }
                    })
                    .catch(e => {
                        return { success: false, batchId: batch._id, error: e.message }
                    })
            })

            const results = await Promise.all(promises)

            const failedUpdates = []
            const successUpdates = []

            results.forEach(result => {
                if (result.success) {
                    successUpdates.push(result.name)
                } else {
                    failedUpdates.push(result)
                }
            })

            if (!!failedUpdates.length) {
                Modal.info({
                    title: 'Kết quả giao may',
                    content: (
                        <div>
                            {successUpdates.length > 0 && (
                                <div className="mt-2">
                                    <h5 className="text-success">Giao may thành công:</h5>
                                    <ul className="pl-4 list-disc">
                                        {successUpdates.map(name => (
                                            <li key={name}>{name}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {failedUpdates.length > 0 && (
                                <div className="mt-2">
                                    <h5 className="text-danger">Có lỗi khi giao may:</h5>
                                    <ul className="pl-4 list-disc">
                                        {failedUpdates.map(i => (
                                            <li key={i.batchId}>{`${i.batchName}: ${i.error}`}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    ),
                    okText: 'Đóng'
                })
            } else {
                message.success('Giao may thành công.')
            }
        } catch (error) {
            message.error(error.message)
        } finally {
            _resetScanInput()
            setDetailBatch({})
            setBatchs([])
        }
    }

    const _handleSubmit = () => {
        if (batchs.length > 1) return _handleMultipleDelivery()

        _handleDelivery()
    }

    const _removeBatch = (batchId) => {
        const newBatchs = batchs.filter(batch => batch._id !== batchId) || []
        if (newBatchs.length === 0) {
            setBatchs(newBatchs)
            _resetScanInput()
            setDetailBatch({})
        } else {
            setBatchs(newBatchs)
            _getSewingThreadWarning(newBatchs.map(b => b.name))
        }
    }

    return (
        <div className='SewingGroupPage'>
            <div className="position-relative">
                <Spin spinning={loading} tip='Đang kiểm tra...'>
                    <form onSubmit={_onSubmit} className="formQC">
                        <Input
                            value={valueInput.toUpperCase()}
                            style={{ fontSize: 40, height: "auto" }}
                            ref={_input}
                            autoFocus={true}
                            placeholder='Nhập vào tên lô'
                            onChange={_handleChangeScan}
                        />

                        {
                            valueInput &&
                            <Icon
                                type='close-circle'
                                className="position-absolute cursor-pointer text-dark IconInput"
                                onClick={_resetScanInput}
                            />
                        }

                        {
                            loading &&
                            <Icon type="sync" spin className="position-absolute IconInput" />
                        }
                    </form>

                    {errorInput && <div className="text-danger mt-3">{errorInput}</div>}

                    {!!Object.keys(detailBatch).length && <div className="sewing-group mt-3">
                        <div className="sewing-group-info" style={{ gridTemplateColumns: batchs.length === 1 ? '1fr 1fr 1.5fr 1fr 1fr' : '1fr' }}>
                            <div className="batch_tag">
                                {
                                    batchs.length > 0 &&
                                    batchs.map((batch) => {
                                        const { count_assignable_sewing_items = 0 } = Object.assign({}, batch)
                                        return (
                                            <Tag
                                                key={batch._id}
                                                closable onClose={() => _removeBatch(batch._id)}
                                            >
                                                {!!count_assignable_sewing_items ? `${batch.name} (${count_assignable_sewing_items})` : batch.name}
                                            </Tag>
                                        )
                                    })
                                }
                            </div>
                            {
                                batchs.length === 1 &&
                                <>
                                    <div className="partner">
                                        <span>Đối tác may: {!!sewing_partner.name ? sewing_partner.name : "Chưa có"}</span>
                                    </div>

                                    <div className="type">
                                        {!!product_types.length && <span>Type: {product_types.join(', ')}</span>}
                                        {!!brand && <span className='mr-3 brand'>{brand}</span>}
                                    </div>

                                    <div className="quantity">
                                        <span>Số lượng: {count_assignable_sewing_items}</span>
                                    </div>

                                    <div className="tags">
                                        {
                                            tags.map(tag => (
                                                <div className={`mr-3 mb-1 tag ${tag}`}>{convertTag(tag)}</div>
                                            ))
                                        }
                                    </div>
                                </>
                            }
                        </div>

                        <div className="sewing-group-list mt-4 pb-4">
                            <h4 className='mb-3'>Chọn nhóm may</h4>
                            <div className="sewing-groups">
                                {
                                    sewingGroups.map((item, index) => (
                                        <div className="sewing-group-item mb-3">
                                            <span onKeyDownCapture={e => _handleKeyDownSelect(e, item)}>
                                                <Select
                                                    value={item.id || []}
                                                    className='custom-select-sewing mr-3'
                                                    placeholder="Chọn nhóm may"
                                                    open={openSelect === item._idx}
                                                    onDropdownVisibleChange={open => _handleDropdownVisibleChange(open, item)}
                                                    style={{ width: '250px' }}
                                                    dropdownMatchSelectWidth={false}
                                                    dropdownRender={menu => !groupColumns.length ? menu : (
                                                        <div className="sewing-group-columns" onMouseDown={e => e.preventDefault()}>
                                                            {
                                                                groupColumns.map(column => (
                                                                    <div className="sewing-group-column" key={column.partnerName}>
                                                                        {!!column.partnerName && <div className="sewing-group-column-title" title={column.partnerName}>{column.partnerName}</div>}
                                                                        {
                                                                            column.groups.map(g => {
                                                                                const disabled = _isGroupDisabled(g._id, item._idx)
                                                                                const selected = item.id === g._id
                                                                                const active = activeOption === g._id

                                                                                return (
                                                                                    <div
                                                                                        key={g._id}
                                                                                        ref={el => { _optionRefs.current[`${item._idx}_${g._id}`] = el }}
                                                                                        className={`sewing-group-option${selected ? ' selected' : ''}${active ? ' active' : ''}${disabled ? ' disabled' : ''}`}
                                                                                        onMouseEnter={() => !disabled && setActiveOption(g._id)}
                                                                                        onClick={() => _handleSelectGroup(g._id, item)}
                                                                                    >
                                                                                        {g.name}
                                                                                    </div>
                                                                                )
                                                                            })
                                                                        }
                                                                    </div>
                                                                ))
                                                            }
                                                        </div>
                                                    )}
                                                >
                                                    {
                                                        listGroups.map(g => (
                                                            <Select.Option key={g._id} value={g._id}>
                                                                {!sewing_partner.name && !!g.partner_name ? `${g.name} (${g.partner_name})` : g.name}
                                                            </Select.Option>
                                                        ))
                                                    }
                                                </Select>
                                            </span>

                                            <span onKeyDownCapture={_handleKeyDownQuantity}>
                                                <InputNumber
                                                    ref={el => { _quantityRefs.current[item._idx] = el }}
                                                    value={batchs.length > 1 ? total : (item.quantity || [])}
                                                    className='custom-input-sewing'
                                                    placeholder="Nhập số lượng"
                                                    disabled={batchs.length > 1}
                                                    onChange={value => _handleChangeSewingGroup('quantity', +value, item._idx)}
                                                    style={{ width: '250px' }}
                                                />
                                            </span>
                                            {
                                                sewingGroups.length > 1 &&
                                                <i
                                                    className="fa fa-trash ml-3 cursor-pointer"
                                                    style={{ fontSize: 20, color: 'red' }}
                                                    onClick={() => _handleDeleteGroupSewing(item._idx)}
                                                />
                                            }
                                        </div>
                                    ))
                                }
                            </div>
                            {
                                batchs.length === 1 &&
                                <Button type='default' style={{ marginLeft: "20.8%" }} onClick={_handleAddGroupSewing}>Thêm nhóm may</Button>
                            }
                        </div>

                        <div className="action-sewing border-top pt-4 d-flex justify-content-end">
                            <Button
                                type='primary'
                                onClick={_handleSubmit}
                                disabled={batchs.length > 1 ? (!!sewingGroups[0] && !sewingGroups[0].id) : !detailBatch._id}
                            >
                                Giao nhóm may
                            </Button>
                        </div>

                        {sewingThreadWarning && sewingThreadWarning.total_items > 0 &&
                            <SewingThreadWarning data={sewingThreadWarning} />
                        }
                    </div>}
                </Spin>

            </div>
        </div>
    )
}

export default SewingGroupPage